/** The HERE platform (API key, services). `H` is what `loadHereMaps` resolved with. */
export const createHerePlatform = (H: any): any =>
  new H.service.Platform({
    apikey: 'F4w9igF1EWxSSpOAIVLllKcOycY9ahp6OMpqj-XctK4',
  });

/**
 * One interactive HERE map in `element`: vector base layer, pan/zoom behaviour and the default UI.
 * Shared by every map in the app so the key and the setup live in one place.
 */
export const createHereMap = (H: any, element: HTMLElement, platform: any = createHerePlatform(H)): any => {
  const defaultLayers = platform.createDefaultLayers();

  const map = new H.Map(element, defaultLayers.vector.normal.map, {
    center: { lat: 50, lng: 5 },
    zoom: 4,
    pixelRatio: window.devicePixelRatio || 1,
  });

  // MapEvents enables the event system; Behavior implements pan/zoom (also on touch). Neither handle is needed afterwards.
  new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
  H?.ui?.UI?.createDefault(map, defaultLayers);

  return map;
};

/**
 * The road route a truck drives through `points` in the given order, as HERE flexible polylines (one
 * per section between two consecutive points). Rejects when HERE cannot route it or refuses the request.
 */
export const calculateTruckRoute = (platform: any, points: { lat: number; lng: number }[]): Promise<string[]> =>
  new Promise((resolve, reject) => {
    if (points.length < 2) return resolve([]);
    const H = (window as any).H;
    const asParam = (point: { lat: number; lng: number }) => `${point.lat},${point.lng}`;
    const via = points.slice(1, -1).map(asParam);

    platform.getRoutingService(null, 8).calculateRoute(
      {
        transportMode: 'truck',
        routingMode: 'fast',
        origin: asParam(points[0]),
        destination: asParam(points[points.length - 1]),
        ...(via.length > 0 ? { via: new H.service.Url.MultiValueQueryParameter(via) } : {}),
        return: 'polyline',
      },
      (result: any) => {
        const polylines = (result?.routes?.[0]?.sections ?? []).map((section: any) => section.polyline).filter(Boolean);
        if (polylines.length > 0) resolve(polylines);
        else reject(new Error(`HERE returned no route${result?.notices ? `: ${JSON.stringify(result.notices)}` : ''}`));
      },
      (error: unknown) => reject(error),
    );
  });
